import React, { useState } from 'react';
import Card from '../Card'; // Importing Card directly
import './Binder.css'; // Adjust styles here to support horizontal scrolling

const Binder = ({ listings }) => {
    const [selectedGrades, setSelectedGrades] = useState([]);

    // Helper function to get earliest year for a group of listings
    const getEarliestYear = (listings) => {
        const years = listings.map(listing => {
            // For promo cards, use the promo release date if available
            if (listing.cert?.promo?.release_date) {
                return new Date(listing.cert.promo.release_date).getFullYear();
            }
            // Otherwise use the set release date
            const releaseDate = listing.cert?.card?.set?.releaseDate;
            return releaseDate ? new Date(releaseDate).getFullYear() : null;
        }).filter(year => year !== null);

        return years.length > 0 ? Math.min(...years) : Infinity;
    };

    // Helper function to get earliest month for a group of listings
    const getEarliestMonth = (listings) => {
        const months = listings.map(listing => {
            // For promo cards, use the promo release date if available
            if (listing.cert?.promo?.release_date) {
                return new Date(listing.cert.promo.release_date).getMonth();
            }
            // Otherwise use the set release date
            const releaseDate = listing.cert?.card?.set?.releaseDate;
            return releaseDate ? new Date(releaseDate).getMonth() : null;
        }).filter(month => month !== null);

        return months.length > 0 ? Math.min(...months) : Infinity;
    };

    // Helper function to get latest year for a group of listings
    const getLatestYear = (listings) => {
        const years = listings.map(listing => {
            // For promo cards, use the promo release date if available
            if (listing.cert?.promo?.release_date) {
                return new Date(listing.cert.promo.release_date).getFullYear();
            }
            // Otherwise use the set release date
            const releaseDate = listing.cert?.card?.set?.releaseDate;
            return releaseDate ? new Date(releaseDate).getFullYear() : null;
        }).filter(year => year !== null);

        return years.length > 0 ? Math.max(...years) : -Infinity;
    };

    // Helper function to check if a group has a date range
    const hasDateRange = (listings) => {
        const years = listings.map(listing => {
            if (listing.cert?.promo?.release_date) {
                return new Date(listing.cert.promo.release_date).getFullYear();
            }
            const releaseDate = listing.cert?.card?.set?.releaseDate;
            return releaseDate ? new Date(releaseDate).getFullYear() : null;
        }).filter(year => year !== null);

        return years.length > 0 && Math.min(...years) !== Math.max(...years);
    };

    // Group all listings by their type (promo or rarity)
    const groupedListings = listings.reduce((acc, listing) => {
        // Check if it's a promo card
        if (listing.cert?.promo?.display_name || listing.cert?.card?.promo?.display_name) {
            const promoName = listing.cert?.promo?.display_name || listing.cert?.card?.promo?.display_name;
            if (!acc[promoName]) {
                acc[promoName] = {
                    type: 'promo',
                    name: promoName,
                    listings: []
                };
            }
            acc[promoName].listings.push(listing);
        } else {
            // Regular card - group by rarity
            const rarity = listing.cert?.card?.rarity || 'Unknown';
            if (!acc[rarity]) {
                acc[rarity] = {
                    type: 'rarity',
                    name: rarity,
                    listings: []
                };
            }
            acc[rarity].listings.push(listing);
        }
        return acc;
    }, {});

    // Convert to array and sort by earliest year, then by date range
    const sortedGroups = Object.values(groupedListings).sort((a, b) => {
        const yearA = getEarliestYear(a.listings);
        const yearB = getEarliestYear(b.listings);
        
        // First sort by earliest year
        if (yearA !== yearB) {
            return yearB - yearA;
        }
        
        // If same earliest year, prioritize groups with date ranges
        const aHasRange = hasDateRange(a.listings);
        const bHasRange = hasDateRange(b.listings);
        if (aHasRange !== bHasRange) {
            return aHasRange ? -1 : 1;
        }
        
        // If both have same type (both ranges or both single years), sort by rarity
        if (a.name !== b.name) {
            return a.name.localeCompare(b.name);
        }
        
        // Final tiebreaker: sort by earliest month
        const monthA = getEarliestMonth(a.listings);
        const monthB = getEarliestMonth(b.listings);
        return monthB - monthA;
    });

    // Filter listings by grade if grades are selected
    const filterByGrade = (listings, selectedGrades) => {
        if (selectedGrades.length === 0) return listings;
        return listings.filter(listing => {
            const grade = listing.cert?.grade;
            if (!grade) return false;
            
            return selectedGrades.some(selectedGrade => {
                if (selectedGrade === '7') {
                    const gradeMatch = grade.match(/\d+/);
                    if (!gradeMatch) return false;
                    const gradeNum = parseInt(gradeMatch[0]);
                    return !isNaN(gradeNum) && gradeNum <= 7;
                }
                return grade.includes(selectedGrade);
            });
        });
    };

    // Helper function to get year range for a group of listings
    const getYearRange = (listings) => {
        const years = listings.map(listing => {
            // For promo cards, use the promo release dates if available
            if (listing.cert?.promo?.release_date) {
                const startYear = new Date(listing.cert.promo.release_date).getFullYear();
                const endDate = listing.cert.promo.release_date_end;
                if (!endDate) {
                    return `${startYear}-Present`;
                }
                const endYear = new Date(endDate).getFullYear();
                return startYear === endYear ? startYear : `${startYear}-${endYear}`;
            }
            // Otherwise use the set release date
            const releaseDate = listing.cert?.card?.set?.releaseDate;
            return releaseDate ? new Date(releaseDate).getFullYear() : null;
        }).filter(year => year !== null);

        if (years.length === 0) return '';
        
        // If all years are the same, just show that year
        const uniqueYears = [...new Set(years)];
        if (uniqueYears.length === 1) {
            return `(${uniqueYears[0]})`;
        }
        
        // Otherwise show the full range
        const minYear = Math.min(...years.map(y => {
            if (typeof y === 'string') {
                return parseInt(y.split('-')[0]);
            }
            return y;
        }));
        
        const maxYear = Math.max(...years.map(y => {
            if (typeof y === 'string') {
                if (y.includes('Present')) return Infinity;
                return parseInt(y.split('-')[1]);
            }
            return y;
        }));
        
        return `(${minYear}-${maxYear === Infinity ? 'Present' : maxYear})`;
    };

    // Deal grade order for sorting
    const dealGradeOrder = ["A+", "A", "A-", "B+", "B", "B-", "C+", "C", "C-", "--"];
    function getDealGradeForSort(listing) {
        let dealGrade = '--';
        let priceChartingValue = null;
        const cardGrade = listing.cert?.grade || listing.cert?.card?.grade;
        const pricingData = listing.cert?.pricing_data;
        const price = listing.price;
        if (pricingData && cardGrade) {
            let field = null;
            if (typeof cardGrade === 'string') {
                if (cardGrade.includes('10')) field = 'psa_10';
                else if (cardGrade.includes('9')) field = 'psa_9';
                else if (cardGrade.includes('8')) field = 'psa_8';
                else if (cardGrade.includes('7')) field = 'psa_7';
            } else if (typeof cardGrade === 'number') {
                field = `psa_${cardGrade}`;
            }
            if (field && pricingData[field]) {
                priceChartingValue = pricingData[field];
                const diff = price - priceChartingValue;
                const pct = diff / priceChartingValue;
                if (pct <= 0) dealGrade = 'A+';
                else if (pct <= 0.05) dealGrade = 'A';
                else if (pct <= 0.10) dealGrade = 'A-';
                else if (pct <= 0.15) dealGrade = 'B+';
                else if (pct <= 0.20) dealGrade = 'B';
                else if (pct <= 0.25) dealGrade = 'B-';
                else if (pct <= 0.30) dealGrade = 'C+';
                else if (pct <= 0.35) dealGrade = 'C';
                else if (pct <= 0.40) dealGrade = 'C-';
                else dealGrade = '--';
            }
        }
        return dealGrade;
    }

    const RaritySection = ({ title, listings }) => {
        const [selectedGrades, setSelectedGrades] = useState([]);
        const yearRange = getYearRange(listings);

        const toggleGrade = (grade) => {
            setSelectedGrades(prev => 
                prev.includes(grade) 
                    ? prev.filter(g => g !== grade)
                    : [...prev, grade]
            );
        };

        const sortedListings = filterByGrade(listings, selectedGrades)
            .map((listing, idx) => ({ listing, originalIdx: idx }))
            .sort((a, b) => {
                const gradeA = getDealGradeForSort(a.listing);
                const gradeB = getDealGradeForSort(b.listing);
                const scoreA = dealGradeOrder.indexOf(gradeA);
                const scoreB = dealGradeOrder.indexOf(gradeB);
                if (scoreA !== scoreB) return scoreA - scoreB;
                return a.originalIdx - b.originalIdx;
            })
            .map(obj => obj.listing);

        return (
            <div className="rarity-section">
                <div className="rarity-header">
                    <h2 className="rarity-title">
                        {title} <i className="year-range">{yearRange}</i>
                    </h2>
                    <div className="grade-filters">
                        <button 
                            className={`grade-filter ${selectedGrades.includes('10') ? 'active' : ''}`}
                            onClick={() => toggleGrade('10')}
                        >
                            10
                        </button>
                        <button 
                            className={`grade-filter ${selectedGrades.includes('9') ? 'active' : ''}`}
                            onClick={() => toggleGrade('9')}
                        >
                            9
                        </button>
                        <button 
                            className={`grade-filter ${selectedGrades.includes('8') ? 'active' : ''}`}
                            onClick={() => toggleGrade('8')}
                        >
                            8
                        </button>
                        <button 
                            className={`grade-filter ${selectedGrades.includes('7') ? 'active' : ''}`}
                            onClick={() => toggleGrade('7')}
                        >
                            7+
                        </button>
                    </div>
                </div>
                <div className="binder-scroll">
                    {sortedListings.map((listing, index) => (
                        <Card key={index} card={listing.cert} price={listing.price} />
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="binder">
            {sortedGroups.map((group) => (
                <RaritySection 
                    key={group.name} 
                    title={group.name} 
                    listings={group.listings} 
                />
            ))}
        </div>
    );
};

export default Binder;
